import React, { useState, useRef, useEffect } from "react";
import { FiX, FiPlus, FiTrash2, FiCheck, FiChevronDown } from "react-icons/fi";
import SkillBadge from "../../../common/skillBadge";

const truncateWithEllipsis = (str, maxLength = 24) => {
    if (!str) return "";
    return str.length > maxLength ? str.slice(0, maxLength - 1) + "…" : str;
};

// Prepare display string for a language entry (show language_label + (level_label))
const getLangProfString = (langObj) => {
    if (!langObj) return "";
    const langName = langObj.language_label || langObj.language || langObj.label || langObj.name || "";
    const proficiency = langObj.level_label || langObj.level_id || langObj.proficiency || langObj.proficiency_code || "";
    if (proficiency) {
        return `${langName} (${proficiency})`;
    }
    return langName || JSON.stringify(langObj);
};

const LanguageSelector = ({
    languages,
    onChange,
    options,
    isEditing,
    languageLevels = [],
    isLanguageLevelLoading
}) => {
    // We make local state using the array of language objects passed in (not the API format)
    const [isAdding, setIsAdding] = useState(false);
    const [newLanguage, setNewLanguage] = useState({ language_id: "", level_id: "" });
    const [showDropdown, setShowDropdown] = useState(false);
    const [isOther, setIsOther] = useState(false);
    const [otherInput, setOtherInput] = useState("");
    const [duplicateError, setDuplicateError] = useState(""); // NEW: track duplicate error
    const dropdownRef = useRef(null);

    // Setup close dropdown on click-away
    useEffect(() => {
        if (!showDropdown) return;
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [showDropdown]);

    // Remove language by index and update via onChange (ensure array is always array of "spec" objects)
    const removeLanguage = (index) => {
        const updated = [...(languages || [])];
        updated.splice(index, 1);
        onChange(updated);
    };

    // This finds the language option for a given id
    const getLanguageOptionById = (idOrValue) => {
        return (Array.isArray(options) ? options : []).find(
            opt =>
                String(opt.value) === String(idOrValue) ||
                String(opt.code) === String(idOrValue) ||
                String(opt.id) === String(idOrValue)
        );
    };

    // This finds the proficiency option for a given id or value
    const getLevelOptionById = (idOrValue) => {
        return (Array.isArray(languageLevels) ? languageLevels : []).find(
            lvl =>
                String(lvl.value) === String(idOrValue) ||
                String(lvl.label) === String(idOrValue)
        );
    };

    // NEW: Check if language already exists
    const isDuplicateLanguage = (languageIdOrLabel) => {
        if (!languageIdOrLabel) return false;
        const normalizedNew = String(languageIdOrLabel).toLowerCase().trim();
        
        return (languages || []).some(lang => {
            const existingId = String(lang.language_id || "").toLowerCase().trim();
            const existingLabel = String(lang.language_label || "").toLowerCase().trim();
            return existingId === normalizedNew || existingLabel === normalizedNew;
        });
    };

    // Invoked when user clicks save in the add row
    const saveNewLanguage = () => {
        setDuplicateError(""); // Clear previous error
        
        let langToSave;
        if (isOther) {
            // User-typed/custom language
            if (!otherInput.trim() || !newLanguage.level_id) return;
            
            // NEW: Check for duplicate
            if (isDuplicateLanguage(otherInput.trim())) {
                setDuplicateError("This language has already been added");
                return;
            }
            
            let selectedLevel = getLevelOptionById(newLanguage.level_id);

            langToSave = {
                language_id: otherInput.trim(),
                language_label: otherInput.trim(),
                level_id: selectedLevel ? selectedLevel.value : newLanguage.level_id,
                level_label: selectedLevel ? selectedLevel.label : newLanguage.level_id,
                __other_language: true
            };
        } else {
            // Predefined language
            const selectedLang = getLanguageOptionById(newLanguage.language_id);
            if (!selectedLang || !newLanguage.level_id) return;

            // NEW: Check for duplicate
            const langId = selectedLang.value || selectedLang.code || selectedLang.id;
            if (isDuplicateLanguage(langId)) {
                setDuplicateError("This language has already been added");
                return;
            }

            let selectedLevel = getLevelOptionById(newLanguage.level_id);

            langToSave = {
                language_id: langId,
                language_label: selectedLang.label || selectedLang.language || selectedLang.name,
                level_id: selectedLevel ? selectedLevel.value : newLanguage.level_id,
                level_label: selectedLevel ? selectedLevel.label : newLanguage.level_id,
                __other_language: false
            };
        }
        if (!langToSave.language_label) return;
        const updated = [...(languages || []), langToSave];
        onChange(updated);
        setIsAdding(false);
        setNewLanguage({ language_id: "", level_id: "" });
        setIsOther(false);
        setOtherInput("");
        setDuplicateError(""); // Clear error on success
    };

    const cancelAdd = () => {
        setIsAdding(false);
        setNewLanguage({ language_id: "", level_id: "" });
        setIsOther(false);
        setOtherInput("");
        setDuplicateError(""); // NEW: Clear error on cancel
    };

    const startAdd = () => {
        setIsAdding(true);
        setIsOther(false);
        setOtherInput("");
        setNewLanguage({ language_id: "", level_id: "" });
        setDuplicateError(""); // NEW: Clear error when starting
    };

    // Prepare the select options, prepend Other
    const optionsWithOther = [{ label: "Other", value: "__other__" }, ...(Array.isArray(options) ? options : [])];

    // Always render as badge + overflow if more than one language
    const langs = languages || [];
    const firstLang = langs[0];
    const restCount = langs.length > 1 ? langs.length - 1 : 0;

    // DISPLAY MODE
    if (!isEditing) {
        return (
            <div className="flex items-center gap-2 relative">
                {firstLang ? (
                    <SkillBadge>
                        <span className="lg:max-w-[5rem] xl:max-w-[18rem] md:max-w-[2rem]"
                            title={getLangProfString(firstLang)}
                            style={{
                                display: "inline-block",
                                verticalAlign: "bottom",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                            }}
                        >
                            {truncateWithEllipsis(getLangProfString(firstLang), 24)}
                        </span>
                    </SkillBadge>
                ) : <div className="text-gray-333 text-xs">Nil</div>}
                {restCount > 0 && (
                    <div className="relative" ref={dropdownRef}>
                        <button
                            type="button"
                            className="text-xs px-2 py-1 rounded-full mph-primary mph-light flex items-center gap-1"
                            onClick={() => setShowDropdown((v) => !v)}
                        >
                            +{restCount}
                            <FiChevronDown size={14} />
                        </button>
                        {showDropdown && (
                            <div className="absolute left-0 mt-2 z-20 bg-white border border-gray-200 rounded shadow-lg min-w-[180px]">
                                {(langs.slice(1)).map((lang, idx) => (
                                    <div
                                        key={idx}
                                        className="px-3 py-1 text-xs mph-primary hover:shadow-md"
                                        title={getLangProfString(lang)}
                                        style={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            maxWidth: 200
                                        }}
                                    >
                                        {truncateWithEllipsis(getLangProfString(lang), 24)}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // EDIT MODE
    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2 relative">
                {firstLang && (
                    <div className="text-sm">
                        <SkillBadge>
                            <span
                                title={getLangProfString(firstLang)}
                                style={{
                                    display: "inline-block",
                                    verticalAlign: "bottom",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {truncateWithEllipsis(getLangProfString(firstLang), 24)}
                                <button
                                    type="button"
                                    onClick={() => removeLanguage(0)}
                                    className="ml-1 p-0 border-none bg-transparent cursor-pointer align-middle"
                                    style={{ verticalAlign: "middle" }}
                                >
                                    <FiX size={12} />
                                </button>
                            </span>
                        </SkillBadge>
                    </div>
                )}
                {restCount > 0 && (
                    <div className="relative" ref={dropdownRef}>
                        <button
                            type="button"
                            className="text-xs px-2 py-1 rounded-full mph-primary mph-light flex items-center gap-1"
                            onClick={() => setShowDropdown((v) => !v)}
                        >
                            +{restCount}
                            <FiChevronDown size={14} />
                        </button>
                        {showDropdown && (
                            <div className="absolute left-[-37px] mt-2 z-20 bg-white border border-gray-200 rounded shadow-lg min-w-[200px]">
                                {(langs.slice(1)).map((lang, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center justify-between mph-primary px-3 py-1 text-xs"
                                        title={getLangProfString(lang)}
                                        style={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            maxWidth: 200
                                        }}
                                    >
                                        <span>
                                            {truncateWithEllipsis(getLangProfString(lang), 24)}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeLanguage(idx + 1)}
                                            className="ml-1 p-0 border-none bg-transparent cursor-pointer"
                                        >
                                            <FiX size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {isAdding && (
                <div className="space-y-2">
                    <div className="flex items-center gap-2 p-2 shadow-md rounded">
                        <div className="flex-1">
                            {!isOther ? (
                                <select
                                    value={newLanguage.language_id}
                                    onChange={e => {
                                        setDuplicateError(""); // Clear error on change
                                        if (e.target.value === "__other__") {
                                            setIsOther(true);
                                            setNewLanguage({ ...newLanguage, language_id: "" });
                                            setOtherInput("");
                                        } else {
                                            setIsOther(false);
                                            setNewLanguage({ ...newLanguage, language_id: e.target.value });
                                            setOtherInput("");
                                        }
                                    }}
                                    className="w-full text-xs p-1 border border-gray-300 rounded"
                                >
                                    <option value="">Select Language</option>
                                    {optionsWithOther.map((option, i) => (
                                        <option key={i} value={option.value || option.code || option.id || option}>
                                            {option.label || option.language || option.name || String(option)}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    value={otherInput}
                                    onChange={e => {
                                        setDuplicateError(""); // Clear error on change
                                        setOtherInput(e.target.value);
                                    }}
                                    placeholder="Enter language"
                                    className="w-full text-xs p-1 border border-gray-300 rounded"
                                    autoFocus
                                />
                            )}
                        </div>
                        <div className="flex-1">
                            <select
                                value={newLanguage.level_id}
                                onChange={e => {
                                    setDuplicateError(""); // Clear error on change
                                    setNewLanguage({ ...newLanguage, level_id: e.target.value });
                                }}
                                className="w-full text-xs p-1 border border-gray-300 rounded"
                                disabled={isLanguageLevelLoading}
                            >
                                <option value="">Select Proficiency</option>
                                {(Array.isArray(languageLevels) ? languageLevels : []).map((level) => (
                                    <option key={level.value} value={level.value}>
                                        {level.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button
                            type="button"
                            onClick={saveNewLanguage}
                            className="text-green-600 hover:text-green-800"
                            title="Save"
                        >
                            <FiCheck size={16} />
                        </button>
                        <button
                            type="button"
                            onClick={cancelAdd}
                            className="text-red-500 hover:text-red-700"
                            title="Cancel"
                        >
                            <FiTrash2 size={16} />
                        </button>
                    </div>
                    {/* NEW: Display duplicate error message */}
                    {duplicateError && (
                        <div className="text-xs text-red-500 px-2">
                            {duplicateError}
                        </div>
                    )}
                </div>
            )}

            {!isAdding && (
                <button
                    type="button"
                    onClick={startAdd}
                    className="mph-primary font-semibold text-xs flex items-center gap-1"
                >
                    <FiPlus /> Add Language
                </button>
            )}
        </div>
    );
};

export default LanguageSelector;